import React, { useContext, useMemo, useState, useRef, useEffect } from 'react';
import { useLabels, usePrefs, useRequestData } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';
import { IncentivesContext, FiltersContext } from '../contexts';
import { filterVehicles, getShowcaseCategories } from '../utils/filterVehicles';
import { clearAllEvent } from '../utils/wueTracking';
import useDeepLinking from '../hooks/useDeepLinking';
import useScrollRestore from '../hooks/useScrollRestore';
import FacetGroupsSection from '../components/FacetGroupsSection';
import AlertBanner from '../components/AlertBanner';
import SkeletonCard from '../components/SkeletonCard';
import EmptyState from '../components/EmptyState';
import VehicleCard from '../components/VehicleCard';
import GroupedVehicleGrid from '../components/Showcase/GroupedVehicleGrid';
import ActiveFilterChips from '../components/ActiveFilterChips';
import MobileFilterFlyout from '../components/MobileFilterFlyout';

const DefaultView = () => {
	useDeepLinking(); // Story 2.8 will implement; wiring now prevents DefaultView changes later
	useScrollRestore();

	const prefs = usePrefs();
	const labels = useLabels();
	const { widgetName, windowId } = useRequestData();
	const { incentives, loading, errors, showcaseMappings } =
		useContext(IncentivesContext);
	const { activeFilters, alertState, clearAll } = useContext(FiltersContext);
	const [flyoutOpen, setFlyoutOpen] = useState(false);
	const [sortOrder, setSortOrder] = useState('relevance');
	const triggerRef = useRef(null);
	const flyoutHasBeenOpen = useRef(false);

	// BR-FACET-001: client-side filtering against FiltersContext.activeFilters
	// BR-VEH-001: incentives.byVehicle is already year-filtered by ContextProviders
	const filteredVehicles = useMemo(
		() => filterVehicles(incentives?.byVehicle, activeFilters),
		[incentives?.byVehicle, activeFilters]
	);

	// BR-VEH-002: pin highlighted models to top of results
	const highlightedModelsList = useMemo(() => {
		const raw = prefs.highlightedModels || '';
		return raw ? raw.split(',').map((m) => m.trim().toLowerCase()) : [];
	}, [prefs.highlightedModels]);

	const sortedVehicles = useMemo(() => {
		if (sortOrder === 'year-desc') {
			return [...filteredVehicles].sort(
				(a, b) => parseInt(b.year, 10) - parseInt(a.year, 10)
			);
		}
		if (sortOrder === 'year-asc') {
			return [...filteredVehicles].sort(
				(a, b) => parseInt(a.year, 10) - parseInt(b.year, 10)
			);
		}
		if (sortOrder === 'make-asc') {
			return [...filteredVehicles].sort((a, b) =>
				(a.make || '').localeCompare(b.make || '')
			);
		}
		// 'relevance': pin highlighted models
		if (!highlightedModelsList.length) return filteredVehicles;
		const highlighted = filteredVehicles.filter((v) =>
			highlightedModelsList.includes(v.model?.toLowerCase())
		);
		const rest = filteredVehicles.filter(
			(v) => !highlightedModelsList.includes(v.model?.toLowerCase())
		);
		return [...highlighted, ...rest];
	}, [filteredVehicles, highlightedModelsList, sortOrder]);

	// BR-SM-003: showcaseCategories only includes vehicles matching at least one
	// MSS mapping rule. Uncategorized vehicles are intentionally excluded — the
	// results header count uses unique globalVehicleIds to avoid double-counting
	// vehicles that appear in multiple categories.
	const showcaseCategories = useMemo(
		() =>
			getShowcaseCategories(incentives, activeFilters, showcaseMappings),
		[incentives, activeFilters, showcaseMappings]
	);
	const isShowcaseMode =
		showcaseCategories !== null && showcaseCategories.length > 0;

	const sortOptions = useMemo(
		() => [
			{
				value: 'relevance',
				label: labels.get('INCENTIVES_SORT_RELEVANCE')
			},
			{
				value: 'year-desc',
				label: labels.get('INCENTIVES_SORT_YEAR_NEWEST')
			},
			{
				value: 'year-asc',
				label: labels.get('INCENTIVES_SORT_YEAR_OLDEST')
			},
			{ value: 'make-asc', label: labels.get('INCENTIVES_SORT_MAKE') }
		],
		[labels]
	);

	const displayCount = useMemo(() => {
		if (isShowcaseMode) {
			const seen = new Set();
			let count = 0;
			showcaseCategories.forEach((cat) =>
				cat.vehicles.forEach((v) => {
					if (!seen.has(v.globalVehicleId)) {
						seen.add(v.globalVehicleId);
						count += v.offerCount || 0;
					}
				})
			);
			return count;
		}
		return sortedVehicles.reduce((sum, v) => sum + (v.offerCount || 0), 0);
	}, [isShowcaseMode, showcaseCategories, sortedVehicles]);

	// Restore focus to trigger button when flyout closes (guard against initial mount)
	useEffect(() => {
		if (flyoutOpen) {
			flyoutHasBeenOpen.current = true;
		} else if (flyoutHasBeenOpen.current && triggerRef.current) {
			triggerRef.current.focus();
		}
	}, [flyoutOpen]);

	if (loading) {
		return (
			<div className="incentives-search ddc-page-section">
				<SkeletonCard count={6} />
			</div>
		);
	}

	if (
		errors?.length > 0 ||
		!Object.keys(incentives?.byVehicle || {}).length
	) {
		return (
			<div className="incentives-search ddc-page-section">
				<EmptyState type="noIncentives" />
			</div>
		);
	}

	if (sortedVehicles.length === 0) {
		return (
			<div className="incentives-search ddc-page-section">
				<EmptyState type="noResults" />
			</div>
		);
	}

	// BR-SM-003: showcase configured but all vehicles uncategorized → empty state
	if (showcaseCategories !== null && showcaseCategories.length === 0) {
		return (
			<div className="incentives-search ddc-page-section">
				<EmptyState type="noResults" />
			</div>
		);
	}

	const resultsCountLabel = labels.get('INCENTIVES_RESULTS_COUNT');
	const resultsSubtitleLabel = labels.get('INCENTIVES_SELECT_VEHICLE');

	return (
		<div className="incentives-search ddc-page-section">
			{alertState && <AlertBanner />}
			<div className="d-md-none">
				<button
					ref={triggerRef}
					type="button"
					className="btn btn-default rounded-pill mobile-filter-trigger w-100 mb-3"
					onClick={() => setFlyoutOpen(true)}
				>
					<i
						className="ddc-icon ddc-icon-filter-list-controls mr-1"
						aria-hidden="true"
					/>
					{labels.get('INCENTIVES_FILTER_SORT')}
				</button>
				<MobileFilterFlyout
					isOpen={flyoutOpen}
					onClose={() => setFlyoutOpen(false)}
					sortValue={sortOrder}
					sortOptions={sortOptions}
					onSortChange={(e) => setSortOrder(e.target.value)}
				/>
			</div>
			<div className="row">
				<div className="col-md-3 d-none d-md-block">
					<FacetGroupsSection />
				</div>
				<div className="col-md-9 col-sm-12">
					<header
						className="results-header mb-3"
						aria-live="polite"
						aria-atomic="true"
					>
						<h1 className="ddc-font-size-xlarge font-weight-bold mb-0">
							{resultsCountLabel.replace('{count}', displayCount)}
						</h1>
						<p className="text-muted ddc-font-size-base mb-0">
							{resultsSubtitleLabel}
						</p>
					</header>
					<div className="d-none d-md-flex align-items-center">
						<ActiveFilterChips />
						{Object.values(activeFilters).some(
							(arr) => arr.length > 0
						) && (
							<button
								type="button"
								className="btn btn-link ddc-font-size-small p-0 text-nowrap ml-3"
								onClick={() => {
									trackEvent(
										widgetName,
										windowId,
										clearAllEvent({
											label: labels.get(
												'INCENTIVES_CLEAR_FILTERS'
											)
										})
									);
									clearAll();
								}}
							>
								{labels.get('INCENTIVES_CLEAR_FILTERS')}
							</button>
						)}
					</div>
					{isShowcaseMode ? (
						<GroupedVehicleGrid categories={showcaseCategories} />
					) : (
						<div className="row d-flex flex-wrap">
							{sortedVehicles.map((vehicle) => (
								<div
									key={vehicle.globalVehicleId}
									className="col-md-4 col-sm-6 col-xs-12 d-flex p-3"
								>
									<VehicleCard vehicle={vehicle} />
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default DefaultView;
